import { checkIsMaintenanceMode } from '@shared/lib/downForMaintenance';
import parsePermissionSafeJson from '@shared/lib/parsePermissionSafeJson';
import showServerError from '@shared/lib/showError';
import i18nInitialState from '@shared/config/translations';
import { logout } from 'redux_store/api';
import { updateIsMaintenanceMode } from 'redux_store/slices';
import { recordSessionHeaders } from '@shared/lib/idleSession';
import { notifyAuthenticatedResponse } from '@shared/lib/authenticatedResponse';
// Store is attached at bootstrap time by @app/store.
// This breaks the circular dependency:
//   client.js → @app/store → slices → api/endpoints → client.js
// See Redux Toolkit docs: "Accessing the store outside of React".
let _store = null;
export const attachStore = store => {
  _store = store;
};

const API_ROOT_URI = `${process.env.REACT_APP_API_URL}`;

export async function client(
  endpoint,
  { body, method, dispatch: externalDispatch, ...customConfig } = {}
) {
  const dispatch =
    externalDispatch ||
    (_store
      ? _store.dispatch
      : () => {
          // eslint-disable-next-line no-console
          console.warn('[api/client] dispatch called before store was attached');
        });
  const isFormData = body instanceof FormData;

  const headers = {
    'Authorization-SetAsCookie': 'true',
    // Browser must set Content-Type for FormData so it can include the
    // multipart boundary. Setting it ourselves breaks the upload.
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    Client: 'dashboard',
    'Cache-Control': 'no-store, no-cache, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  };

  const config = {
    method: method || 'GET',
    ...customConfig,
    headers: {
      ...headers,
      ...customConfig.headers,
    },
    credentials: 'include',
  };

  if (body !== undefined && body !== null) {
    config.body = isFormData ? body : JSON.stringify(body);
  }

  let data;
  try {
    const response = await window.fetch(`${API_ROOT_URI}${endpoint}`, config);
    // Not response.json(): permission masks above 2^53 must be quoted
    // before parsing or JSON.parse corrupts them (see the helper).
    data = parsePermissionSafeJson(await response.text());

    if (response.status === 500 && data.errorCode === 2000) {
      dispatch(logout());
    }
    // Session headers (JWT deadline, idle length) for features/session-idle.
    const session = recordSessionHeaders(response.headers);
    // A successful authenticated request: the alerts hub may (re)start.
    if (session.authenticated && response.ok) notifyAuthenticatedResponse();

    if (response.status === 401 || response.status === 403) {
      showServerError(dispatch, i18nInitialState.en.notifications.noPermissions);
      return { data: null, status: response.status, ok: false };
    }

    // 410: the server-side session is over. The idle feature handles its
    // own calls (`silent410`) so the login page gets the idle message
    // instead of the generic one.
    if (response.status === 410 && !customConfig.silent410) {
      window.location.href = '/login#logout';
    }

    if (!response.ok) {
      const maintenanceResponse = await checkIsMaintenanceMode();
      if (maintenanceResponse.status === 200) {
        dispatch(updateIsMaintenanceMode(true));
      }
    }

    return {
      data,
      status: response.status,
      ok: response.ok,
    };
  } catch (err) {
    return Promise.reject(err.message ? err.message : data);
  }
}

client.get = function (endpoint, body = {}, customConfig = {}) {
  return client(endpoint, { ...customConfig, method: 'GET', dispatch: customConfig.dispatch });
};

client.post = function (endpoint, body, customConfig = {}) {
  return client(endpoint, {
    ...customConfig,
    body,
    method: 'POST',
    dispatch: customConfig.dispatch,
  });
};

client.put = function (endpoint, body, customConfig = {}) {
  return client(endpoint, {
    ...customConfig,
    body,
    method: 'PUT',
    dispatch: customConfig.dispatch,
  });
};

client.patch = function (endpoint, body, customConfig = {}) {
  return client(endpoint, {
    ...customConfig,
    body,
    method: 'PATCH',
    dispatch: customConfig.dispatch,
  });
};

client.delete = function (endpoint, body, customConfig = {}) {
  return client(endpoint, {
    ...customConfig,
    body,
    method: 'DELETE',
    dispatch: customConfig.dispatch,
  });
};

export const apiCallWithRetry = async (apiCall, retries = 3, delay = 1000) => {
  let attempts = 0;
  while (attempts < retries) {
    try {
      return await apiCall(); // Attempt the API call
    } catch (error) {
      attempts++;
      if (attempts >= retries) throw error; // Throw error after max retries
      await new Promise(resolve => setTimeout(resolve, delay + attempts)); // Delay before retry
    }
  }
};
